import * as React from 'react';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useConsultantTutorialStepDetails } from '../../../shared/api/apiHooks';
import { Icon, Loader } from 'semantic-ui-react';
import { getConsultantTutorialSteps } from '../../../reducers/ConsultantTutorialReducer/ConsultantTutorialSelectors';
import './ConsultantTutorialIndex.scss';
import Parser from 'html-react-parser';
import { MemoizedTutorialProgressBar } from '../TutorialProgressBar';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { useMeasure } from 'react-use';
import { useDebounce } from 'use-debounce';

interface ConsultantTutorialIndexProps {
  currentStepId?: number;
  onStepClick?: (stepId: number) => void;
}

const consultantTutorialChapters = [
  { title: 'Different Type of Alphas', firstStep: 0, lastStep: 4 },
  { title: 'Available features', firstStep: 5, lastStep: 14 },
  { title: 'Compensation', firstStep: 15, lastStep: 16 },
  { title: 'Exclusive events and support for Consultants', firstStep: 17, lastStep: 17 },
];

interface TaskProps {
  isCompleted?: boolean;
}
function Task({ isCompleted = false }: TaskProps) {
  const className = isCompleted ? 'consultant-tutorial-index__task--complete' : 'consultant-tutorial-index__task';
  return <strong className={className}>[Task]</strong>;
}

function ConsultantTutorialIndex({ currentStepId, onStepClick }: ConsultantTutorialIndexProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [ref, { width }] = useMeasure();
  const [debouncedWidth] = useDebounce(width, 200);
  const { isLoading, error } = useConsultantTutorialStepDetails();
  const steps = useSelector(getConsultantTutorialSteps);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  if (isLoading && steps.length === 0) {
    return <Loader active size="small" />;
  }

  if (error || steps.length === 0) {
    return <div className="consultant-tutorial-index__error">Failed to load tutorial index</div>;
  }

  const completeStepCount = steps.filter(step => step.status === 'COMPLETED').length;
  const totalSteps = steps.length;
  const progressGraphColor = 'rgb(0, 174, 0)';

  // @ts-ignore
  const progressLabel = `${completeStepCount}/${totalSteps}`;
  return (
    <>
      {/*@ts-ignore*/}
      <div className="consultant-tutorial-index" ref={ref}>
        <div className="consultant-tutorial-index__header">
          <h3 className="consultant-tutorial-index__title">Consultant Tutorial</h3>
          <button
            className="consultant-tutorial-index__toggle-button"
            onClick={toggleExpanded}
            aria-label={isExpanded ? 'Collapse index' : 'Expand index'}
          >
            <Icon name={isExpanded ? 'chevron up' : 'chevron down'} />
          </button>
        </div>

        <div className="tutorials-status__bar-container">
          <div className="tutorials-status__bar-label">
            <FormattedMessageHelper id={'consultantTutorial.progress'} />
          </div>
          <MemoizedTutorialProgressBar
            value={completeStepCount}
            maxValue={totalSteps}
            progressLabel={progressLabel}
            graphColor={progressGraphColor}
            width={debouncedWidth}
          />
        </div>

        {isExpanded && (
          <div className="consultant-tutorial-index__chapters">
            {consultantTutorialChapters.map((chapter, chapterIndex) => {
              // Filter steps that belong to this chapter
              const chapterSteps = steps.filter(step => step.id >= chapter.firstStep && step.id <= chapter.lastStep);

              return (
                <div key={chapterIndex} className="consultant-tutorial-index__chapter">
                  <h4 className="consultant-tutorial-index__chapter-title">{chapter.title}</h4>
                  <ul className="consultant-tutorial-index__list">
                    {chapterSteps.map(step => {
                      const isCurrentStep = step.id === currentStepId;
                      const hasTask = !!step.task;
                      const isCompleted = step.status === 'COMPLETED';
                      const isVisited = step.visited;

                      return (
                        <li
                          key={step.id}
                          className={`consultant-tutorial-index__item ${isCurrentStep ? 'consultant-tutorial-index__item--current' : ''}`}
                          onClick={() => onStepClick && onStepClick(step.id)}
                        >
                          <div className="consultant-tutorial-index__item-content">
                            <span
                              className={`consultant-tutorial-index__item-title ${
                                isVisited ? 'consultant-tutorial-index__item-title--visited' : ''
                              }`}
                              style={{
                                fontWeight: isCurrentStep ? 'bold' : 'normal',
                                color: isCurrentStep ? '#0074c4' : isVisited ? '#888' : '#000',
                              }}
                            >
                              {hasTask ? (
                                <span>
                                  <Task isCompleted={isCompleted} /> {step.name}
                                </span>
                              ) : (
                                step.name
                              )}
                            </span>
                            <span className="consultant-tutorial-index__item-status">{isCompleted ? '✅' : '⬜'}</span>
                          </div>
                          {hasTask && isCurrentStep && step.task && (
                            <div className="consultant-tutorial-index__item-task">{Parser(step.task)}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default React.memo(ConsultantTutorialIndex);
